<style lang="scss">
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/colors";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/mixins";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/utils";

  .selector-label {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    margin: 2px;

    &.small {
      height: utils.in_rem(24px);
      gap: utils.in_rem(8px);
      .content {
        @include mixins.caption();
      }
    }

    &.large {
      height: utils.in_rem(34px);
      gap: utils.in_rem(12px);
      .content {
        @include mixins.body2();
      }
    }

    .content {
      &.skeleton {
        @include mixins.skeleton(
          (
            border-radius: 4px,
          )
        );
      }
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      @include colors.color(textBlack);

      &.bounce {
        @include colors.color(textDisabledGrey);
      }

      // remove default <a> styling for tel tags
      a {
        cursor: inherit;
        color: inherit;
        text-decoration: none;
      }
    }

    .end-icons {
      display: flex;
      flex-direction: row;
      align-items: flex-end;
      justify-content: flex-end;
    }
  }
</style>

<script>
  import { showToast } from "@sourcewhale/core";
  import { userStore } from "../../../src/svelte/api-stores/user-store";
  import IconButton from "./IconButton.svelte";
  import IconScale from "./IconScale.svelte";

  export let size = "large";
  const iconSize = size === "large" ? "medium" : "small";

  export let label = "";

  export let kind = "email";
  const kindMap = {
    email: "fa-solid fa-envelope flex-0",
    mobile: "fa-regular fa-mobile flex-0",
    landline: "fa-solid fa-phone flex-0",
    other: "fa-solid fa-phone flex-0",
  };

  export let missing = false;
  export let bounce = false;
  export let skeleton = false;

  // If the email is missing, we want to show a grey icon
  // otherwise show a blue icon (for emails and phones)
  const iconProps = missing
    ? {
        backgroundColor: "interfaceSwGrey_5",
        color: "textLighterBlack",
        class: kindMap[kind],
      }
    : {
        backgroundColor: "activityMessage_5",
        color: "activityMessage_50",
        class: kindMap[kind],
      };

  const copyProps = {
    icon: "far fa-copy",
    size: "medium",
    onclick: () => {
      navigator.clipboard.writeText(label).then(
        showToast({
          title: `Copied ${label} to clipboard`,
          type: "success",
        }),
      );
    },
  };

  // For phone flagging
  let focused = false;
  let hovered = false;

  export let flagClick = () => {};
  const flagProps = {
    icon: "far fa-flag",
    size: "small",
    onclick: flagClick,
  };

  const onfocus = () => {
    focused = true;
  };

  const onblur = () => {
    focused = false;
  };

  const onmouseenter = () => {
    hovered = true;
  };

  const onmouseleave = () => {
    hovered = false;
  };

  $: showFlag = focused || hovered;
</script>

<div
  class="selector-label {size}"
  role="button"
  on:focus={onfocus}
  on:blur={onblur}
  on:mouseenter={onmouseenter}
  on:mouseleave={onmouseleave}
  tabindex="0"
  class:skeleton
>
  <IconScale {...iconProps} size={iconSize} {skeleton} />

  {#if !missing && showFlag}
    <IconButton {...flagProps} {skeleton} />
  {/if}

  <div class="content {bounce ? 'bounce' : ''}" class:skeleton>
    <!-- Display phone numbers inside tel tags for IDR only (is a cloudcall issue but tel tags can cause issues for other dialers too)-->
    {#if ["mobile", "landline", "other"].includes(kind) && $userStore.telTagsRequired}
      <span class:skeleton>
        <a on:click|preventDefault href="tel:{label}">{label}</a>
      </span>
    {:else}
      <span class:skeleton>{label}</span>
    {/if}
  </div>
  {#if kind === "email" && bounce}
    <IconScale
      class="fa-regular fa-hand"
      color="systemError_100"
      placement="top"
      size="medium"
      {skeleton}
    >
      <span slot="tooltip">Email bounced</span>
    </IconScale>
  {/if}

  <!-- Only show the copy button if the email/phone is not missing or bounce -->
  {#if !missing && !bounce}
    <div class="end-icons">
      <IconButton {...copyProps} {skeleton} />
    </div>
  {/if}
</div>
